import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { catchError, mergeMap, of } from 'rxjs';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import {
	UsageReportDialogComponent,
	UsageReportFilterOptions,
	UsageReportFilters,
} from '../usage-report-dialog/usage-report-dialog.component';
import { UsageReport } from 'src/app/models/model';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-usage-report-table',
	templateUrl: './usage-report-table.component.html',
	styleUrls: ['./usage-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetLoadingRowComponent,
	],
})
export class UsageReportTableComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns = ['companyName', 'totalAccesses', 'totalDevices'];
	dataSource = new MatTableDataSource<UsageReport>();
	private filterSubscription?: Subscription;

	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	reportFilters: UsageReportFilters;
	filterOptions?: UsageReportFilterOptions;

	breadcrumbLabel = $localize`:@@usageReportTable-breadcrumbLabel:Usage Report`;

	generateUsageReportLoadingKey = ApiLoadingKeys.generateUsageReport;
	generateUsageReportCsvLoadingKey = ApiLoadingKeys.generateUsageReportCsv;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.filterSubscription = this.dataSet?.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generateUsageReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				}),
				catchError(() => {
					this.finishedLoadingInitData = true;
					return of([] as UsageReport[]);
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(UsageReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		const fileName = this.hasFilters ? 'filteredUsageReport' : 'usageReport';
		await this.apiService.generateUsageReportCsv(
			fileName,
			this.compiledFilters
		);
	}

	rowId(row: UsageReport): string {
		return row?.companyName || 'unknown-id';
	}
}
